import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  createSupplierIncomingResult,
  fetchSupplierIncomingTemplateByToken,
} from "@/modules/supplier-quality/api";
import { Button } from "@/shared/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/shared/ui/card";
import { Input } from "@/shared/ui/input";
import { Label } from "@/shared/ui/label";
import { Textarea } from "@/shared/ui/textarea";
import { Badge } from "@/shared/ui/badge";
import { Separator } from "@/shared/ui/separator";
import { ScrollArea } from "@/shared/ui/scroll-area";
import { toast } from "sonner";
import { 
  FileSearch, 
  Upload, 
  CheckCircle2, 
  XCircle, 
  AlertTriangle,
  FileText,
  Building2,
  Loader2,
  Check,
  X
} from "lucide-react";

interface TestResult {
  name: string;
  value: string;
  numeric_value: number | null;
  unit: string;
  specification: string;
  tolerance: string;
  ucl: number | null;
  lcl: number | null;
  passed: boolean | null;
}

interface ExtractedData {
  material_name: string;
  batch_lot_number: string;
  supplier_name: string;
  document_date: string;
  expiry_date: string;
  test_results: TestResult[];
}

// Parse specification/tolerance to extract UCL/LCL
const parseSpecLimits = (spec: string, tolerance: string, value: string): { ucl: number | null; lcl: number | null; nominal: number | null } => {
  let ucl: number | null = null;
  let lcl: number | null = null;
  let nominal: number | null = parseFloat(value) || null;
  
  const combined = `${spec || ''} ${tolerance || ''}`.trim();
  
  // Pattern: "10-15" or "10 - 15" (range)
  const rangeMatch = combined.match(/(\d+(?:\.\d+)?)\s*[-–]\s*(\d+(?:\.\d+)?)/);
  if (rangeMatch) {
    lcl = parseFloat(rangeMatch[1]);
    ucl = parseFloat(rangeMatch[2]);
    if (lcl !== null && ucl !== null) {
      nominal = (lcl + ucl) / 2;
    }
    return { ucl, lcl, nominal };
  }
  
  // Pattern: "12 ± 0.5" or "12 +/- 0.5" or "12±0.5"
  const pmMatch = combined.match(/(\d+(?:\.\d+)?)\s*[±\+\-\/]+\s*(\d+(?:\.\d+)?)/);
  if (pmMatch) {
    nominal = parseFloat(pmMatch[1]);
    const delta = parseFloat(pmMatch[2]);
    if (nominal !== null && !isNaN(delta)) {
      ucl = nominal + delta;
      lcl = nominal - delta;
    }
    return { ucl, lcl, nominal };
  }
  
  // Pattern: "min 5" or "min: 5" or "≥5" or ">5" or ">=5"
  const minMatch = combined.match(/(?:min[:\s]*|[≥>]=?\s*)(\d+(?:\.\d+)?)/i);
  if (minMatch) {
    lcl = parseFloat(minMatch[1]);
  }
  
  // Pattern: "max 10" or "max: 10" or "≤10" or "<10" or "<=10"
  const maxMatch = combined.match(/(?:max[:\s]*|[≤<]=?\s*)(\d+(?:\.\d+)?)/i);
  if (maxMatch) {
    ucl = parseFloat(maxMatch[1]);
  }
  
  // If only one number in spec, try to use it as nominal
  if (ucl === null && lcl === null) {
    const singleNumber = spec?.match(/^(\d+(?:\.\d+)?)$/);
    if (singleNumber) {
      nominal = parseFloat(singleNumber[1]);
    }
  }
  
  return { ucl, lcl, nominal };
};

export default function SupplierInspectionPortal() {
  const { token } = useParams<{ token: string }>();
  const queryClient = useQueryClient();
  
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [documentPreviewUrl, setDocumentPreviewUrl] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [extractedData, setExtractedData] = useState<ExtractedData | null>(null);
  const [inspectorNotes, setInspectorNotes] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  
  // Editable fields
  const [editedMaterialName, setEditedMaterialName] = useState("");
  const [editedBatchLotNumber, setEditedBatchLotNumber] = useState("");
  const [editedDocumentDate, setEditedDocumentDate] = useState("");
  const [editedExpiryDate, setEditedExpiryDate] = useState("");
  const [editedTestResults, setEditedTestResults] = useState<TestResult[]>([]);

  // Cleanup object URL on unmount
  useEffect(() => {
    return () => {
      if (documentPreviewUrl) {
        URL.revokeObjectURL(documentPreviewUrl);
      }
    };
  }, [documentPreviewUrl]);

  // Fetch inspection instruction via magic token
  const { data: instruction, isLoading, error } = useQuery({
    queryKey: ["supplier-inspection", token],
    queryFn: async () => {
      if (!token) return null;
      return fetchSupplierIncomingTemplateByToken(token);
    },
    enabled: !!token,
  });

  // Check if token is expired
  const isExpired = instruction?.supplier_magic_token_expires_at 
    ? new Date(instruction.supplier_magic_token_expires_at) < new Date()
    : false;

  // Initialize editable fields when extracted data changes - with smart parsing
  useEffect(() => {
    if (extractedData) {
      setEditedMaterialName(extractedData.material_name || "");
      setEditedBatchLotNumber(extractedData.batch_lot_number || "");
      setEditedDocumentDate(extractedData.document_date || "");
      setEditedExpiryDate(extractedData.expiry_date || "");
      
      // Process test results with smart UCL/LCL parsing
      setEditedTestResults(extractedData.test_results.map(r => {
        const numericValue = r.numeric_value ?? (parseFloat(r.value) || null);
        // Parse spec/tolerance to auto-fill UCL/LCL if not already set
        const parsedLimits = parseSpecLimits(r.specification, r.tolerance, r.value);
        
        // Calculate pass/fail if we have numeric value and limits
        let passed = r.passed;
        if (numericValue !== null && (parsedLimits.ucl !== null || parsedLimits.lcl !== null)) {
          const withinUcl = parsedLimits.ucl === null || numericValue <= parsedLimits.ucl;
          const withinLcl = parsedLimits.lcl === null || numericValue >= parsedLimits.lcl;
          passed = withinUcl && withinLcl;
        }
        
        return {
          ...r,
          numeric_value: numericValue,
          ucl: r.ucl ?? parsedLimits.ucl,
          lcl: r.lcl ?? parsedLimits.lcl,
          passed,
        };
      }));
    }
  }, [extractedData]);

  const processFile = async (file: File) => {
    setUploadedFile(file);
    setIsProcessing(true);
    setExtractedData(null);

    // Create preview URL for the document
    const previewUrl = URL.createObjectURL(file);
    setDocumentPreviewUrl(previewUrl);

    try {
      // Convert file to base64
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result as string;
          resolve(result.split(",")[1]);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      // Call OCR edge function
      const { data, error } = await supabase.functions.invoke("ocr-document", {
        body: {
          imageBase64: base64,
          mimeType: file.type,
        },
      });

      if (error) throw error;

      setExtractedData(data.data);
      toast.success("Document processed successfully");
    } catch (err: any) {
      console.error("OCR error:", err);
      toast.error("Failed to process document: " + err.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await processFile(file);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isProcessing) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (isProcessing) return;

    const file = e.dataTransfer.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
    if (!validTypes.includes(file.type)) {
      toast.error("Invalid file type. Please upload PDF, JPG, or PNG.");
      return;
    }

    await processFile(file);
  };

  const submitMutation = useMutation({
    mutationFn: async () => {
      if (!instruction || !uploadedFile) throw new Error("Missing data");

      // Upload document to storage
      const fileExt = uploadedFile.name.split(".").pop();
      const filePath = `${instruction.factory_id}/${instruction.id}/${Date.now()}.${fileExt}`;
      
      const { error: uploadError } = await supabase.storage
        .from("incoming-inspection-docs")
        .upload(filePath, uploadedFile);

      if (uploadError) throw uploadError;

      // Calculate overall status
      const allPassed = editedTestResults.every(r => r.passed !== false);
      const anyFailed = editedTestResults.some(r => r.passed === false);
      const overallStatus = anyFailed ? "rejected" : allPassed ? "approved" : "pending";

      await createSupplierIncomingResult({
        tenant_id: instruction.tenant_id,
        factory_id: instruction.factory_id,
        template_id: instruction.id,
        submitted_by: null,
        supplier_id: instruction.assigned_supplier?.id ?? null,
        supplier_name: instruction.assigned_supplier?.name ?? null,
        material_name: editedMaterialName || null,
        batch_lot_number: editedBatchLotNumber || null,
        document_date: editedDocumentDate || null,
        expiry_date: editedExpiryDate || null,
        test_results: editedTestResults,
        overall_status: overallStatus,
        document_file_name: uploadedFile.name,
        document_file_path: filePath,
        document_type: "coa",
        inspector_notes: inspectorNotes || null,
      });
    },
    onSuccess: () => {
      toast.success("Inspection submitted successfully!");
      // Reset form
      setUploadedFile(null);
      setDocumentPreviewUrl(null);
      setExtractedData(null);
      setInspectorNotes("");
      setEditedMaterialName("");
      setEditedBatchLotNumber("");
      setEditedDocumentDate("");
      setEditedExpiryDate("");
      setEditedTestResults([]);
    },
    onError: (error) => {
      toast.error("Failed to submit: " + error.message);
    },
  });

  const updateTestResult = (index: number, field: keyof TestResult, value: any) => {
    setEditedTestResults(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      
      // Update numeric_value when value changes
      if (field === 'value') {
        updated[index].numeric_value = parseFloat(value) || null;
      }
      
      // Auto-calculate passed status if numeric_value and limits are set
      if (field === "numeric_value" || field === "ucl" || field === "lcl" || field === 'value') {
        const result = updated[index];
        const numVal = field === 'value' ? (parseFloat(value) || null) : result.numeric_value;
        if (numVal !== null) {
          const withinUcl = result.ucl === null || numVal <= result.ucl;
          const withinLcl = result.lcl === null || numVal >= result.lcl;
          updated[index].passed = withinUcl && withinLcl;
        }
      }
      
      return updated;
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-4">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary" />
          <p className="text-muted-foreground">Loading inspection...</p>
        </div>
      </div>
    );
  }

  if (error || !instruction) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center space-y-4">
            <XCircle className="w-12 h-12 text-destructive mx-auto" />
            <h2 className="text-xl font-semibold">Invalid or Expired Link</h2>
            <p className="text-muted-foreground">
              This inspection link is invalid or has expired. Please contact your customer for a new link.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isExpired) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center space-y-4">
            <AlertTriangle className="w-12 h-12 text-warning mx-auto" />
            <h2 className="text-xl font-semibold">Link Expired</h2>
            <p className="text-muted-foreground">
              This inspection link has expired. Please contact your customer to receive a new link.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10">
              <Building2 className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h1 className="text-lg font-semibold text-foreground">Supplier Inspection Portal</h1>
              <p className="text-sm text-muted-foreground">
                {instruction.assigned_supplier?.name || "Supplier"} - Document Upload
              </p>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-6 space-y-6">
        {/* Inspection Info */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <FileSearch className="w-5 h-5 text-primary" />
              <CardTitle>{instruction.name}</CardTitle>
            </div>
            {instruction.description && (
              <CardDescription>{instruction.description}</CardDescription>
            )}
          </CardHeader>
        </Card>

        {/* Upload Section - shown when no document uploaded yet */}
        {!extractedData && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Upload Certificate</CardTitle>
              <CardDescription>
                Upload your Certificate of Analysis (CoA) or Certificate of Conformance (CoC)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div 
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                  isDragging 
                    ? "border-primary bg-primary/5" 
                    : "border-border hover:border-primary/50"
                }`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={handleFileUpload}
                  className="hidden"
                  id="file-upload"
                  disabled={isProcessing}
                />
                <label
                  htmlFor="file-upload"
                  className="cursor-pointer flex flex-col items-center gap-3"
                >
                  {isProcessing ? (
                    <>
                      <Loader2 className="w-10 h-10 text-primary animate-spin" />
                      <p className="text-muted-foreground">Processing document...</p>
                    </>
                  ) : isDragging ? (
                    <>
                      <Upload className="w-10 h-10 text-primary" />
                      <p className="font-medium text-primary">Drop your file here</p>
                    </>
                  ) : uploadedFile ? (
                    <>
                      <FileText className="w-10 h-10 text-primary" />
                      <p className="font-medium">{uploadedFile.name}</p>
                      <p className="text-sm text-muted-foreground">Click or drag to upload a different file</p>
                    </>
                  ) : (
                    <>
                      <Upload className="w-10 h-10 text-muted-foreground" />
                      <p className="font-medium">Drag and drop or click to upload</p>
                      <p className="text-sm text-muted-foreground">PDF, JPG, or PNG (max 10MB)</p>
                    </>
                  )}
                </label>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Side-by-side layout: Document Preview + Extracted Data */}
        {extractedData && documentPreviewUrl && uploadedFile && (
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Left: Document Preview */}
            <Card className="lg:sticky lg:top-6 lg:self-start">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">Uploaded Document</CardTitle>
                <CardDescription>{uploadedFile.name}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="border rounded-lg overflow-hidden bg-muted/30">
                  {uploadedFile.type === 'application/pdf' ? (
                    <iframe
                      src={documentPreviewUrl}
                      className="w-full h-[calc(100vh-300px)] min-h-[500px]"
                      title="Document preview"
                    />
                  ) : (
                    <img
                      src={documentPreviewUrl}
                      alt="Document preview"
                      className="w-full max-h-[calc(100vh-300px)] min-h-[500px] object-contain"
                    />
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Right: Extracted Data Form */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">Extracted Information</CardTitle>
                <CardDescription>
                  Review and correct the extracted data before submitting
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[calc(100vh-350px)] min-h-[500px] pr-4">
                  <div className="space-y-6">
                    {/* Basic Info */}
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div className="space-y-2">
                        <Label>Material Name</Label>
                        <Input
                          value={editedMaterialName}
                          onChange={(e) => setEditedMaterialName(e.target.value)}
                          placeholder="Material name"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Batch/Lot Number</Label>
                        <Input
                          value={editedBatchLotNumber}
                          onChange={(e) => setEditedBatchLotNumber(e.target.value)}
                          placeholder="Batch or lot number"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Document Date</Label>
                        <Input
                          type="date"
                          value={editedDocumentDate}
                          onChange={(e) => setEditedDocumentDate(e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Expiry Date</Label>
                        <Input
                          type="date"
                          value={editedExpiryDate}
                          onChange={(e) => setEditedExpiryDate(e.target.value)}
                        />
                      </div>
                    </div>

                    <Separator />

                    {/* Test Results */}
                    <div className="space-y-4">
                      <h4 className="font-medium">Test Results ({editedTestResults.length} items)</h4>
                      <div className="space-y-3">
                        {editedTestResults.map((result, index) => (
                          <div
                            key={index}
                            className={`p-3 rounded-lg border space-y-3 ${
                              result.passed === true
                                ? "border-success/30 bg-success/5"
                                : result.passed === false
                                ? "border-destructive/30 bg-destructive/5"
                                : "border-border"
                            }`}
                          >
                            {/* Test Name & Status */}
                            <div className="flex items-center justify-between gap-3">
                              <Input
                                value={result.name}
                                onChange={(e) => updateTestResult(index, "name", e.target.value)}
                                className="font-medium text-sm h-8"
                                placeholder="Test name"
                              />
                              {result.passed !== null && (
                                <Badge 
                                  variant={result.passed ? "default" : "destructive"} 
                                  className="text-xs shrink-0"
                                >
                                  {result.passed ? (
                                    <><CheckCircle2 className="w-3 h-3 mr-1" /> PASS</>
                                  ) : (
                                    <><XCircle className="w-3 h-3 mr-1" /> FAIL</>
                                  )}
                                </Badge>
                              )}
                            </div>
                            
                            {/* Value, Unit, Nominal */}
                            <div className="grid grid-cols-3 gap-2">
                              <div>
                                <Label className="text-xs">Value</Label>
                                <Input
                                  value={result.value}
                                  onChange={(e) => updateTestResult(index, 'value', e.target.value)}
                                  className="h-8 text-xs"
                                  placeholder="Measured"
                                />
                              </div>
                              <div>
                                <Label className="text-xs">Unit</Label>
                                <Input
                                  value={result.unit}
                                  onChange={(e) => updateTestResult(index, 'unit', e.target.value)}
                                  className="h-8 text-xs"
                                  placeholder="Unit"
                                />
                              </div>
                              <div>
                                <Label className="text-xs">Nominal</Label>
                                <Input
                                  value={result.specification}
                                  onChange={(e) => updateTestResult(index, 'specification', e.target.value)}
                                  className="h-8 text-xs"
                                  placeholder="Target"
                                />
                              </div>
                            </div>
                            
                            {/* UCL, LCL, Tolerance */}
                            <div className="grid grid-cols-3 gap-2">
                              <div>
                                <Label className="text-xs">LCL</Label>
                                <Input
                                  type="number"
                                  step="any"
                                  value={result.lcl ?? ""}
                                  onChange={(e) => updateTestResult(index, "lcl", e.target.value ? parseFloat(e.target.value) : null)}
                                  className="h-8 text-xs"
                                  placeholder="Lower"
                                />
                              </div>
                              <div>
                                <Label className="text-xs">UCL</Label>
                                <Input
                                  type="number"
                                  step="any"
                                  value={result.ucl ?? ""}
                                  onChange={(e) => updateTestResult(index, "ucl", e.target.value ? parseFloat(e.target.value) : null)}
                                  className="h-8 text-xs"
                                  placeholder="Upper"
                                />
                              </div>
                              <div>
                                <Label className="text-xs">Tolerance</Label>
                                <Input
                                  value={result.tolerance}
                                  onChange={(e) => updateTestResult(index, 'tolerance', e.target.value)}
                                  className="h-8 text-xs"
                                  placeholder="±0.5"
                                />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <Separator />

                    {/* Notes */}
                    <div className="space-y-2">
                      <Label>Additional Notes (Optional)</Label>
                      <Textarea
                        value={inspectorNotes}
                        onChange={(e) => setInspectorNotes(e.target.value)}
                        placeholder="Any additional notes about this inspection..."
                        rows={3}
                      />
                    </div>

                    {/* Submit Button */}
                    <Button
                      className="w-full"
                      size="lg"
                      onClick={() => submitMutation.mutate()}
                      disabled={submitMutation.isPending || !editedMaterialName || !editedBatchLotNumber}
                    >
                      {submitMutation.isPending ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Submitting...
                        </>
                      ) : (
                        <>
                          <CheckCircle2 className="w-4 h-4 mr-2" />
                          Submit Inspection
                        </>
                      )}
                    </Button>
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </div>
        )}
      </main>
    </div>
  );
}
